import { useEffect, useState } from "react";

import { useSingleBenchmarkMetric } from "app/pages/activation/hooks/useSingleBenchmarkMetric";
import useActivationObjectQuery from "app/pages/activation/queries/data/useActivationObjectQuery";
import { BenchmarkMetricName } from "app/common/benchmark/types";
import type { TrafficShareMetricSchema } from "app/common/benchmark/types";
import { BenchmarkControllers } from "app/pages/benchmark-tracker";

import useSiteMetaInfo from "../queries/useSiteMetaInfo";
import type { FirstPartyDataSource } from "../types/api";
import type { BenchmarkGranularity } from "../types/common";

import { useFormattedDuration } from "./helpers/useFormattedDuration";

const useProjectData = (key: string, country: number, timeGranularity: BenchmarkGranularity) => {
    const { defaultDuration, formattedDuration } = useFormattedDuration(timeGranularity);
    const [firstPartyDataSource, setFirstPartyDataSource] = useState<FirstPartyDataSource>();

    const { data: activationObject, isLoading: isActivationLoading } = useActivationObjectQuery(key, true);

    const { data: metricData, isLoading: isMetricLoading } = useSingleBenchmarkMetric<TrafficShareMetricSchema>(
        BenchmarkMetricName.trafficShare,
        activationObject?.assets?.benchmarkId,
        country,
        defaultDuration,
        timeGranularity,
        BenchmarkControllers.engagement,
        activationObject?.competitorsDomains ?? [],
    );

    const {
        data: siteMeta,
        isFetching: isSiteMetaInfoFetching,
        isLoading: isSiteMetaInfoLoading,
    } = useSiteMetaInfo(activationObject?.primaryDomain, country, isActivationLoading);

    useEffect(() => {
        if (!activationObject) {
            return;
        }

        setFirstPartyDataSource(activationObject.trafficSource);
    }, [activationObject]);

    const isSiteMetaLoading = isSiteMetaInfoFetching || isSiteMetaInfoLoading;
    const isGeneralDataLoading = isActivationLoading || isSiteMetaLoading;
    const isMetricLoaderActive = isMetricLoading || isGeneralDataLoading;

    const isViewerMode = !activationObject?.canEdit;

    return {
        siteMeta,
        activationObject,
        defaultDuration,
        formattedDuration,
        isMetricLoaderActive,
        isGeneralDataLoading,
        metricData,
        firstPartyDataSource,
        setFirstPartyDataSource,
        isViewerMode,
    };
};

export default useProjectData;
